import type { Invite, User } from '@gmn-portal/backend';
import type { ColumnDef, OnChangeFn, SortingState } from '@tanstack/react-table';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useState } from 'react';
import { useIntl } from '@/core/i18n/use-intl';
import { SortableHeader, TableLoadingCell } from '@/shared/components/tables';
import {
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui';

import { UserTableActions } from './user-table-actions';

interface UserTableProps {
  users: (User | (Invite & { isInvite: true }))[];
  onEdit: (user: User | (Invite & { isInvite: true })) => void;
  onDelete: (user: User | (Invite & { isInvite: true })) => void;
  onResend: (user: User | (Invite & { isInvite: true })) => void;
  isLoading?: boolean;
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
}

export const UserTable = ({
  users,
  onEdit,
  onDelete,
  onResend,
  isLoading,
  sorting: externalSorting,
  onSortingChange,
}: UserTableProps) => {
  const { t } = useIntl();
  const [internalSorting, setInternalSorting] = useState<SortingState>([]);

  // Use either external or internal sorting state
  const sorting = externalSorting !== undefined ? externalSorting : internalSorting;

  // Create a handler that works with TanStack's OnChangeFn type
  const handleSortingChange: OnChangeFn<SortingState> = (updaterOrValue) => {
    const newValue =
      typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue;
    if (onSortingChange) {
      onSortingChange(newValue);
    } else {
      setInternalSorting(newValue);
    }
  };

  const columns: ColumnDef<User | (Invite & { isInvite: true })>[] = [
    {
      accessorFn: (row) => `${row.firstName} ${row.lastName}`,
      id: 'name',
      header: ({ column }) => <SortableHeader column={column} title={t('common.name')} />,
      cell: ({ row }) => (
        <div
          className="truncate max-w-[200px]"
          title={`${row.original.firstName} ${row.original.lastName}`}
        >{`${row.original.firstName} ${row.original.lastName}`}</div>
      ),
      size: 200,
      minSize: 150,
      maxSize: 300,
    },
    {
      accessorKey: 'email',
      header: ({ column }) => <SortableHeader column={column} title={t('auth.email')} />,
      cell: ({ row }) => (
        <div className="truncate max-w-[200px]" title={row.getValue('email')}>
          {row.getValue('email')}
        </div>
      ),
      size: 220,
      minSize: 180,
      maxSize: 300,
    },
    {
      accessorKey: 'role',
      header: ({ column }) => <SortableHeader column={column} title={t('users.role')} />,
      cell: ({ row }) => (
        <Badge variant={row.getValue('role') === 'customer-admin' ? 'default' : 'secondary'}>
          {t(`portalRoles.${row.getValue('role')}` as unknown as never)}
        </Badge>
      ),
      size: 150,
      minSize: 120,
      maxSize: 200,
    },
    {
      id: 'status',
      accessorFn: (row) => ('isInvite' in row && row.isInvite ? 'invited' : 'active'),
      header: ({ column }) => <SortableHeader column={column} title={t('common.status')} />,
      cell: ({ row }) => {
        const status = row.getValue('status');
        return (
          <Badge variant={status === 'active' ? 'default' : 'outline-solid'}>
            {status === 'active' ? t('users.active') : t('users.invited')}
          </Badge>
        );
      },
      size: 120,
      minSize: 100,
      maxSize: 150,
    },
    {
      id: 'actions',
      cell: ({ row }) => (
        <UserTableActions
          onEdit={() => onEdit(row.original)}
          onDelete={() => onDelete(row.original)}
          onResend={() => onResend(row.original)}
          isInvite={'isInvite' in row.original && row.original.isInvite}
        />
      ),
      size: 120,
      minSize: 80,
      maxSize: 140,
    },
  ];

  const columnCount = columns.length;

  const table = useReactTable({
    data: users,
    columns,
    state: {
      sorting,
    },
    onSortingChange: handleSortingChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    columnResizeMode: 'onChange',
  });

  return (
    <Table className="w-full table-fixed">
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id} className="group">
            {headerGroup.headers.map((header) => (
              <TableHead
                key={header.id}
                className={header.column.id === 'actions' ? 'text-right' : undefined}
                style={{ width: `${header.getSize()}px` }}
              >
                {header.isPlaceholder
                  ? null
                  : flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {isLoading ? (
          <TableLoadingCell colSpan={columnCount} />
        ) : table.getRowModel().rows?.length ? (
          table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell
                  key={cell.id}
                  className={cell.column.id === 'actions' ? 'text-right' : undefined}
                  style={{ width: `${cell.column.getSize()}px` }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell
              colSpan={columnCount}
              className="h-24 text-center text-muted-foreground text-sm"
            >
              {t('common.noResults')}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
};
